/**
 * src/utils/markerTap.ts
 *
 * Shared tap-handler for custom Mapbox markers.
 *
 * Why this exists:  on iOS Safari (and every WebKit-dressed browser on
 * iOS — Chrome, Edge, Firefox, Brave) a synthetic `click` on a plain
 * <div> created at runtime is only fired when the touch point stays
 * virtually still between touchstart and touchend.  The tiniest finger
 * jitter makes Mapbox claim the gesture as a pan and the click is
 * silently dropped — which is exactly why users tapping a wallet or
 * session marker on an iPhone saw absolutely nothing happen.
 *
 * The fix is to drive our own tap detection off pointer events.  They
 * fire reliably on every platform (mouse, touch, pen), give us the
 * coordinates up front, and let us decide ourselves whether the user
 * performed a tap (small movement, short duration) or a drag.
 *
 * The helper also supports a distinct double-tap action so the same
 * marker can open a profile on single tap and zoom on double tap,
 * which is how player/opponent markers already behave on desktop.
 */

export interface TapHandlerOptions {
  /** Fired when the user performs a deliberate single tap. */
  onTap?: () => void;
  /** Fired when the user performs a double tap within the window. */
  onDoubleTap?: () => void;
  /** Max movement between pointerdown and pointerup, in pixels. */
  moveTolerance?: number;
  /** Max time between pointerdown and pointerup, in milliseconds. */
  maxDuration?: number;
  /** Max time between the first and second tap, in milliseconds. */
  doubleTapWindow?: number;
}

/**
 * Attach a tap/double-tap handler to a DOM element.  Safe to use on
 * any element; designed for Mapbox custom-marker children so they
 * behave the same way a real <button> would on iOS.
 *
 * Returns a cleanup function that removes every listener.
 */
export function attachMarkerTap(el: HTMLElement, opts: TapHandlerOptions): () => void {
  const moveTolerance   = opts.moveTolerance   ?? 10;
  const maxDuration     = opts.maxDuration     ?? 500;
  const doubleTapWindow = opts.doubleTapWindow ?? 300;

  // Ask the browser to skip the 300ms tap-delay and synthetic-click
  // magic.  We handle taps ourselves below — this just makes the
  // native gesture feel snappier on touch devices.
  el.style.touchAction = 'manipulation';

  let downX = 0, downY = 0, downTime = 0;
  let lastTapTime = 0;
  let singleTapTimer: ReturnType<typeof setTimeout> | null = null;

  const onPointerDown = (e: PointerEvent) => {
    downX = e.clientX; downY = e.clientY; downTime = Date.now();
  };

  const onPointerUp = (e: PointerEvent) => {
    const dx = Math.abs(e.clientX - downX);
    const dy = Math.abs(e.clientY - downY);
    const dt = Date.now() - downTime;
    // User was panning / long-pressing — let Mapbox have it.
    if (dx > moveTolerance || dy > moveTolerance || dt > maxDuration) return;
    e.stopPropagation();

    const now = Date.now();
    const timeSinceLast = now - lastTapTime;
    lastTapTime = now;

    if (opts.onDoubleTap && timeSinceLast < doubleTapWindow) {
      if (singleTapTimer) { clearTimeout(singleTapTimer); singleTapTimer = null; }
      lastTapTime = 0;
      opts.onDoubleTap();
      return;
    }

    if (opts.onTap) {
      if (opts.onDoubleTap) {
        // Debounce: wait in case a follow-up tap is coming.
        if (singleTapTimer) clearTimeout(singleTapTimer);
        singleTapTimer = setTimeout(() => {
          singleTapTimer = null;
          opts.onTap?.();
        }, doubleTapWindow);
      } else {
        opts.onTap();
      }
    }
  };

  // Swallow synthetic clicks so Mapbox's map-level click handler
  // doesn't double-fire and close the thing we just opened.
  const onClick = (e: Event) => { e.stopPropagation(); };
  const onDblClick = (e: Event) => { e.stopPropagation(); e.preventDefault(); };

  el.addEventListener('pointerdown', onPointerDown);
  el.addEventListener('pointerup', onPointerUp);
  el.addEventListener('click', onClick);
  el.addEventListener('dblclick', onDblClick);

  return () => {
    if (singleTapTimer) clearTimeout(singleTapTimer);
    el.removeEventListener('pointerdown', onPointerDown);
    el.removeEventListener('pointerup', onPointerUp);
    el.removeEventListener('click', onClick);
    el.removeEventListener('dblclick', onDblClick);
  };
}
